import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Copy } from "lucide-react";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

const EditOrderModal = ({ order, closeModal, onOrderUpdated }) => {
  const [items, setItems] = useState(order.items || []);
  const [gst, setGst] = useState(order.gst || 0);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedInwardReference, setSelectedInwardReference] = useState(null);
  const [transporterName, setTransporterName] = useState(
    order.transporterName || ""
  );
  const [transporters, setTransporters] = useState([]);
  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        const token = localStorage.getItem("token");
        const warehouseResponse = await axios.get(
          buildApiUrl("/api/warehouse/inventory/warehouse-details"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setWarehouses(warehouseResponse.data.data);
      } catch (error) {
        console.error("Error fetching warehouse data:", error.message);
      }
    };

    const fetchTransporterData = async () => {
      try {
        const token = localStorage.getItem("token");
        const transporterResponse = await axios.get(
          buildApiUrl("/api/transporter"),
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTransporters(transporterResponse.data);
      } catch (error) {
        console.error("Error fetching transporter data:", error.message);
      }
    };

    fetchTransporterData();
    fetchWarehouseData();
  }, [order.items]);

  const handleCopyRow = (index) => {
    const newItems = [...items];
    const copiedRow = { ...newItems[index] };
    newItems.splice(index + 1, 0, copiedRow);
    setItems(newItems);
    toast.info("Row duplicated successfully", {
      position: "top-right",
      autoClose: 2000,
    });
  };

  const handlePriceChange = (index, value) => {
    const newItems = [...items];
    newItems[index].price = parseFloat(value) || 0;
    setItems(newItems);
  };

  const handleWarehouseChange = (
    index,
    warehouseId,
    inwardReference,
    inwardShipmentMark
  ) => {
    const newItems = [...items];
    newItems[index].wareHouseName = warehouseId;
    newItems[index].inwardReference = inwardReference;
    newItems[index].inwardShipmentMark = inwardShipmentMark;
    setItems(newItems);
    setSelectedWarehouse(warehouseId);
    setSelectedInwardReference(inwardReference);
  };

  const handleGstChange = (e) => {
    setGst(parseFloat(e.target.value) || 0);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    const parsedValue =
      field === "qty" || field === "quantitycarton"
        ? parseInt(value) || 0
        : value;
    newItems[index][field] = parsedValue;

    if (field === "qty") {
      const pcsPerCtn = newItems[index].pcsPerCtn || 1;
      newItems[index]["quantitycarton"] = Math.floor(parsedValue / pcsPerCtn);
    } else if (field === "quantitycarton") {
      const pcsPerCtn = newItems[index].pcsPerCtn || 1;
      newItems[index]["qty"] = parsedValue * pcsPerCtn;
    }
    setItems(newItems);
  };

  const totalItemAmountBeforeGST = items.reduce(
    (total, item) => total + (item.price || 0) * (item.qty || 0),
    0
  );

  const gstAmount = (totalItemAmountBeforeGST * gst) / 100;
  const totalAmountWithGST = totalItemAmountBeforeGST + gstAmount;

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(`/api/orderItem/price/${order._id}`),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: items.map((item) => ({
              categoryName: item.categoryName || "",
              itemName: item.itemName || "",
              itemCode: item.itemCode || "",
              pcsPerCtn: item.pcsPerCtn || 1,
              quantitycarton: item.quantitycarton || 0,
              qty: item.qty || 0,
              price: item.price || 0,
              wareHouseName: item.wareHouseName || "",
              inwardReference: item.inwardReference || "",
              inwardShipmentMark: item.inwardShipmentMark || "",
            })),
            transporterName: transporterName || "",
            gst: gst || 0,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update order");
      }

      const updatedOrder = await response.json();
      
      // Show toast message first
      toast.success("Order updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Update parent component
      onOrderUpdated(updatedOrder);
      
      // Close modal after a small delay to ensure toast is visible
      setTimeout(() => {
        closeModal();
      }, 100);
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const getAvailableWarehouses = (item) => {
    return warehouses.reduce((availableWarehouses, warehouse) => {
      const warehouseItems = warehouse.items.filter(
        (warehouseItem) => warehouseItem.itemCode === item.itemCode
      );
      warehouseItems.forEach((warehouseItem) => {
        availableWarehouses.push({
          warehouseId: warehouse._id,
          inwardReference: warehouseItem.inwardReference,
          inwardShipmentMark: warehouseItem.inwardShipmentMark,
          quantity: warehouseItem.quantity,
        });
      });
      return availableWarehouses;
    }, []);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900/70 flex items-center justify-center p-2 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 10 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl overflow-hidden"
        >
          <div className="px-3 py-2 bg-gradient-to-r ">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">
                Edit Order
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-800 hover:text-gray-900 transition-colors duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="max-h-[75vh] overflow-y-auto px-3 pb-4">
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-600">
                Transporter Name
              </label>
              <Select
                value={
                  transporters.find(
                    (t) => t.transporterName === transporterName
                  ) || null
                }
                onChange={(selected) =>
                  setTransporterName(selected ? selected.transporterName : "")
                }
                options={transporters}
                getOptionLabel={(option) => option.transporterName}
                getOptionValue={(option) => option.transporterName}
                className="w-full sm:w-1/3 text-xs"
                classNamePrefix="react-select"
                placeholder="Select Transporter"
                isClearable
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: "28px",
                    height: "28px",
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "28px",
                    padding: "0 4px",
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: 0,
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "28px",
                  }),
                }}
              />
            </div>
            <div className="space-y-1">
              <div className="text-xs bg-gray-200 py-1 rounded-md text-gray-700 font-medium hidden sm:grid grid-cols-1 sm:grid-cols-[repeat(7,_1fr)_20px_80px] gap-2">
                <span className="pl-2">Category</span>
                <span>Item</span>
                <span>Code</span>
                <span>Qty</span>
                <span>Carton</span>
                <span>Price</span>
                <span>Warehouse</span>
                <span></span>
                <span></span>
              </div>
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="grid grid-cols-1 sm:grid-cols-[repeat(7,_1fr)_20px_80px] gap-2 items-center bg-gray-50 rounded-md p-2 sm:p-0 transition-colors duration-200"
                >
                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Category
                    </label>
                    <input
                      readOnly
                      type="text"
                      value={item.categoryName || ""}
                      onChange={(e) =>
                        handleItemChange(index, "categoryName", e.target.value)
                      }
                      className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Item
                    </label>
                    <input
                      readOnly
                      type="text"
                      value={item.itemName || ""}
                      onChange={(e) =>
                        handleItemChange(index, "itemName", e.target.value)
                      }
                      className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Code
                    </label>
                    <input
                      readOnly
                      type="text"
                      value={item.itemCode || ""}
                      onChange={(e) =>
                        handleItemChange(index, "itemCode", e.target.value)
                      }
                      className="w-full px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Qty
                    </label>
                    <input
                      type="number"
                      value={item.qty || ""}
                      onChange={(e) =>
                        handleItemChange(index, "qty", e.target.value)
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Carton
                    </label>
                    <input
                      type="number"
                      value={item.quantitycarton || ""}
                      onChange={(e) =>
                        handleItemChange(
                          index,
                          "quantitycarton",
                          e.target.value
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Price
                    </label>
                    <input
                      type="number"
                      value={item.price || ""}
                      onChange={(e) => handlePriceChange(index, e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label className="block sm:hidden text-xs font-medium text-gray-600">
                      Warehouse
                    </label>
                    <select
                      value={item.wareHouseName || ""}
                      onChange={(e) =>
                        handleWarehouseChange(
                          index,
                          e.target.value,
                          e.target.selectedOptions[0].dataset.inwardReference,
                          e.target.selectedOptions[0].dataset.inwardShipmentMark
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-colors duration-200"
                    >
                      <option value="">Select Warehouse</option>
                      {getAvailableWarehouses(item).map((warehouse, idx) => {
                        const isOutOfStock =
                          warehouse.quantity < (item.qty || 0);
                        return (
                          <option
                            key={idx}
                            value={warehouse.warehouseId}
                            data-inward-reference={warehouse.inwardReference}
                            data-inward-shipment-mark={
                              warehouse.inwardShipmentMark
                            }
                            disabled={isOutOfStock}
                            className={
                              isOutOfStock ? "text-red-500" : "text-gray-800"
                            }
                          >
                            {warehouse.warehouseId} -{" "}
                            {isOutOfStock ? "Out of Stock" : "In Stock"} (
                            {warehouse.quantity})
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="cursor-pointer self-end mb-1"
                    onClick={() => handleCopyRow(index)}
                    title="Duplicate Item"
                  >
                    <Copy className="w-4 h-4 text-blue-500 hover:text-blue-700 transition-colors duration-200" />
                  </motion.div>

                  {item.wareHouseName && (
                    <div className="text-xs text-gray-500">
                      <div>Ref: {item.inwardReference}</div>
                      <div>Ship: {item.inwardShipmentMark}</div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-2 space-y-1 bg-gray-50 p-2 rounded-md">
              <div className="flex justify-end text-lg gap-4">
                <span className="font-medium  text-gray-700">Total:</span>
                <span className="text-blue-600 font-semibold">
                  ₹{totalItemAmountBeforeGST.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                className="w-fit mt-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-2 rounded-md text-xs font-medium shadow-sm hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
              >
                Update Order
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default EditOrderModal;
